import * as React from "react";
import { CircleCheckIcon, InfoIcon, Trash2Icon, TriangleAlertIcon, type LucideIcon } from "lucide-react";

import { cn } from "../../lib/utils";
import { Dialog, DialogContent, DialogDescription, DialogTitle } from "./dialog";

/** What the confirmation is about: sets the icon tint and the colour of the primary button (board S2). */
export enum ConfirmDialogTone {
  /** Deletes or removes something: red icon, red button, focus starts on Annuler. */
  Destructive = "destructive",
  /** Information or a neutral confirmation: blue icon, dark button. */
  Info = "info",
  /** Risky but reversible: amber icon, dark button. */
  Warning = "warning",
  /** Something good happens (activate, approve): green icon, green button. */
  Positive = "positive",
}

const TONE_STYLES: Record<ConfirmDialogTone, { icon: LucideIcon; badge: string; button: string }> = {
  [ConfirmDialogTone.Destructive]: {
    icon: Trash2Icon,
    badge: "bg-[#fef2f2] text-[#dc2626] dark:bg-red-500/15 dark:text-red-400",
    button: "bg-[#dc2626] text-white hover:bg-[#b91c1c]",
  },
  [ConfirmDialogTone.Info]: {
    icon: InfoIcon,
    badge: "bg-[#f0f9ff] text-[#0369a1] dark:bg-sky-500/15 dark:text-sky-300",
    button: "bg-[#17181c] text-white hover:bg-[#2c2f36] dark:bg-foreground dark:text-background dark:hover:bg-foreground/90",
  },
  [ConfirmDialogTone.Warning]: {
    icon: TriangleAlertIcon,
    badge: "bg-[#fffbeb] text-[#b45309] dark:bg-amber-500/15 dark:text-amber-300",
    button: "bg-[#17181c] text-white hover:bg-[#2c2f36] dark:bg-foreground dark:text-background dark:hover:bg-foreground/90",
  },
  [ConfirmDialogTone.Positive]: {
    icon: CircleCheckIcon,
    badge: "bg-[#ecfdf5] text-[#059669] dark:bg-emerald-500/15 dark:text-emerald-400",
    button: "bg-primary text-white hover:bg-[#047857]",
  },
};

const BUTTON_BASE =
  "w-full cursor-pointer rounded-full p-[11px] text-[14.5px] leading-5 transition-colors outline-none focus-visible:ring-3 focus-visible:ring-ring/50 disabled:pointer-events-none disabled:opacity-50";

type ConfirmActionDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  title: string;
  description: string;
  confirmLabel?: string;
  cancelLabel?: string;
  /** Kept for existing callers: `destructive` defaults the tone to `ConfirmDialogTone.Destructive`. */
  confirmVariant?: "default" | "destructive";
  /** Icon tint and primary button colour. Defaults from `confirmVariant` (destructive, otherwise info). */
  tone?: ConfirmDialogTone;
  /** Overrides the tone's default icon (trash, info, warning, check). */
  icon?: LucideIcon;
  contentTestId?: string;
  confirmTestId?: string;
  cancelTestId?: string;
  /** @deprecated Ignored: every confirmation is the same 380px card (board S2). */
  size?: "default" | "sm";
  pending?: boolean;
  onConfirm: () => void;
};

/**
 * Information / confirmation dialog, board S2: a centred 380px card, a big tinted round icon on top, the title,
 * one or two lines of text, then full-width stacked buttons, the primary one first and Annuler under it as a
 * muted pill. On a phone it is the app's bottom sheet with the same content.
 */
export function ConfirmActionDialog({
  open,
  onOpenChange,
  title,
  description,
  confirmLabel = "Confirm",
  cancelLabel = "Cancel",
  confirmVariant = "default",
  tone,
  icon,
  contentTestId,
  confirmTestId,
  cancelTestId,
  pending = false,
  onConfirm,
}: ConfirmActionDialogProps) {
  const resolvedTone =
    tone ?? (confirmVariant === "destructive" ? ConfirmDialogTone.Destructive : ConfirmDialogTone.Info);
  const styles = TONE_STYLES[resolvedTone];
  const Icon = icon ?? styles.icon;
  const cancelRef = React.useRef<HTMLButtonElement>(null);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        size='sm'
        showCloseButton={false}
        mobileFullscreen={false}
        className='rounded-[16px] border-0 !shadow-[0_24px_60px_-12px_rgba(16,24,40,0.35)] sm:max-w-[380px]'
        bodyClassName='flex flex-col items-center gap-2.5 px-6 pt-[26px] pb-5 text-center'
        data-testid={contentTestId}
        onOpenAutoFocus={(event) => {
          // Destructive: Enter must never delete by accident, so focus starts on the safe button.
          if (resolvedTone !== ConfirmDialogTone.Destructive) return;
          event.preventDefault();
          cancelRef.current?.focus();
        }}
      >
        <span className={cn("flex size-[52px] shrink-0 items-center justify-center rounded-full", styles.badge)}>
          <Icon aria-hidden className='size-[22px] [stroke-width:2]' />
        </span>
        <DialogTitle className='text-foreground text-lg leading-6 font-bold'>{title}</DialogTitle>
        <DialogDescription className='text-[13.5px] leading-5 text-[#4b4d5a] dark:text-muted-foreground'>
          {description}
        </DialogDescription>
        <div className='mt-2 flex w-full flex-col gap-2'>
          <button
            type='button'
            className={cn(BUTTON_BASE, "font-extrabold", styles.button)}
            onClick={onConfirm}
            disabled={pending}
            data-testid={confirmTestId}
          >
            {confirmLabel}
          </button>
          <button
            ref={cancelRef}
            type='button'
            className={cn(
              BUTTON_BASE,
              "bg-[#f2f3f5] font-bold text-[#17181c] dark:text-foreground hover:bg-[#e8e9ec] dark:bg-muted dark:hover:bg-muted/80",
            )}
            onClick={() => onOpenChange(false)}
            disabled={pending}
            data-testid={cancelTestId}
          >
            {cancelLabel}
          </button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
